import { useState } from "react";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Copy, CheckCheck } from "lucide-react";


const briefSchemaReference = [
  {
    title: "Root Object (Brief JSON)",
    description: "The top-level configuration object for a wizard. Each brief targets ONE region (or none). Region metadata is set at the root level — there is no region picker step. Product data comes from the separate Product JSON.",
    example: `{
  "version": "1.0",
  "region_key": "nordics_no",
  "region_label": "Norway",
  "regionPromptFragment": "A Norwegian home setting — timber-clad walls...",
  "steps": [...],
  "constraints": {...},
  "promptTemplate": "...{{_region_context}}...{{_product_description}}...",
  "promptFragments": { "zone_key": { "interior_living_space": "..." } }
}`,
    fields: [
      { name: "version", type: "string", required: true, desc: "Config schema version, e.g. \"1.0\"" },
      { name: "region_key", type: "string | null", required: false, desc: "Region identifier (e.g. \"nordics_no\"). Null for region-free briefs." },
      { name: "region_label", type: "string | null", required: false, desc: "Human-readable region name (e.g. \"Norway\"). Null for region-free briefs." },
      { name: "regionPromptFragment", type: "string", required: false, desc: "Descriptive text injected into the prompt via {{_region_context}}. Empty string for region-free briefs." },
      { name: "steps", type: "Step[]", required: true, desc: "Ordered array of wizard steps" },
      { name: "constraints", type: "object", required: false, desc: "Category-based rules to hide options or set defaults" },
      { name: "promptTemplate", type: "string", required: true, desc: "Template string with {{field_key}} placeholders. Region context via {{_region_context}}. Product data via {{_product_description}}, {{_product_details}}, {{_reference_image_list}}. Photographer direction via {{_photographer_direction}}. Reference-image direction via {{_brief_reference_direction}}, which resolves to the step\u2019s referenceInstruction only when the user uploaded an image, and to an empty string otherwise." },
      { name: "promptFragments", type: "object", required: true, desc: "Maps fieldKey → optionKey → descriptive text. Only for Brief-owned fields (scene, season, presence). Photographer fragments live in the Photographer JSON." },
    ],
  },
  {
    title: "Per-Region Architecture",
    description: "Each Brief JSON targets a single region or is region-free. The region is metadata, not a wizard step. This allows region-specific home types, conditions, and prompt context without bloating a single config.",
    example: `// Norway brief — region-specific cabin types in conditionalOptions
{
  "region_key": "nordics_no",
  "region_label": "Norway",
  "regionPromptFragment": "A Norwegian home setting — timber-clad walls, fjord-inspired light...",
  "steps": [
    {
      "id": "context",
      "fields": [{
        "key": "household_type",
        "conditionalOptions": {
          "region_nordics_no": [
            { "key": "mountain_cabin", "label": "Mountain Cabin (Fjellhytte)" }
          ]
        }
      }]
    }
  ]
}

// Region-free brief — for products that don't need location context
{
  "region_key": null,
  "region_label": null,
  "regionPromptFragment": "",
  "steps": [...]
}`,
    fields: [
      { name: "region_key", type: "string | null", required: false, desc: "Set to a region key for region-specific briefs. Null for global/region-free." },
      { name: "regionPromptFragment", type: "string", required: false, desc: "Baked into the prompt via {{_region_context}}. Describes architecture, vegetation, light." },
      { name: "conditionalOptions", type: "object", required: false, desc: "Use region_{key} prefix for region-specific options that always apply when this brief is active." },
      { name: "{{_region_context}}", type: "placeholder", required: false, desc: "In promptTemplate, replaced with regionPromptFragment at assembly time." },
    ],
  },
  {
    title: "Step Object",
    description: "Each step represents one screen in the wizard. The type determines how it renders.",
    example: `{
  "id": "context",
  "title": "Domestic Context",
  "subtitle": "What kind of home and room?",
  "icon": "home",
  "type": "grouped_select",
  "fields": [...]
}`,
    fields: [
      { name: "id", type: "string", required: true, desc: "Unique step identifier" },
      { name: "title", type: "string", required: true, desc: "Step heading shown to the user" },
      { name: "subtitle", type: "string", required: true, desc: "Helper text below the title" },
      { name: "icon", type: "string", required: true, desc: "Lucide icon key (e.g. \"home\", \"sun\", \"camera\")" },
      { name: "type", type: "string", required: true, desc: "Renderer type — see Step Types below" },
      { name: "fieldKey", type: "string", required: false, desc: "Answer key for single-field steps (tile_select, textarea, reference_image). For reference_image it MUST start with _reference_image." },
      { name: "fields", type: "Field[]", required: false, desc: "Array of field groups for grouped_select and dual_select types" },
      { name: "options", type: "Option[]", required: false, desc: "Direct options array for tile_select" },
      { name: "placeholder", type: "string", required: false, desc: "Placeholder text for textarea type; helper text under the upload button for reference_image" },
      { name: "referenceInstruction", type: "string", required: false, desc: "reference_image only. What the model should take from the uploaded image and what to ignore. Surfaces via {{_brief_reference_direction}}." },
      { name: "multiple", type: "boolean", required: false, desc: "reference_image only. Allow several uploads (max 3 are sent to the model)." },
    ],
  },
  {
    title: "Step Types",
    description: "The type field on a Step determines how options are rendered.",
    example: null,
    fields: [
      { name: "tile_select", type: "type", required: false, desc: "Simple single-select grid of option cards. Uses fieldKey for the answer key and options array." },
      { name: "grouped_select", type: "type", required: false, desc: "Multiple field groups on one step. Supports conditionalOptions and showWhen." },
      { name: "dual_select", type: "type", required: false, desc: "Two side-by-side field groups (e.g. Season + Time of Day)." },
      { name: "textarea", type: "type", required: false, desc: "Free-text input. Uses fieldKey and placeholder." },
      { name: "reference_image", type: "type", required: false, desc: "Asks the user to upload an image that guides generation — a style to match, a scene to improve, a composition to expand. Attached to the request after the product photography, so product identity still comes from the product images. Optional: generation works without an upload." },
    ],
  },
  {
    title: "Option Object",
    description: "A selectable choice within a step or field.",
    example: `{
  "key": "mountain_cabin",
  "label": "Mountain Cabin (Fjellhytte)",
  "emoji": "🏔️",
  "description": "Alpine cabin with timber, stone fireplace, mountain views"
}`,
    fields: [
      { name: "key", type: "string", required: true, desc: "Unique value stored as the answer" },
      { name: "label", type: "string", required: true, desc: "Display text shown on the card" },
      { name: "description", type: "string", required: false, desc: "Helper text below the label" },
      { name: "emoji", type: "string", required: false, desc: "Emoji displayed before the label" },
      { name: "image", type: "string", required: false, desc: "Relative path to image in wizard-assets bucket (future)" },
      { name: "locked", type: "boolean", required: false, desc: "If true, option is visible but dimmed and non-selectable" },
    ],
  },
  {
    title: "Field Object (grouped_select / dual_select)",
    description: "A named group of options within a step.",
    example: `{
  "key": "zone_key",
  "label": "Zone",
  "options": [...],
  "conditionalOptions": { "zone_utility": [...], "region_nordics_no": [...] },
  "showWhen": { "field": "presence_level", "values": ["background", "active"] }
}`,
    fields: [
      { name: "key", type: "string", required: true, desc: "Answer key for this field group" },
      { name: "label", type: "string", required: true, desc: "Section label shown above options" },
      { name: "options", type: "Option[]", required: true, desc: "Default options always shown" },
      { name: "conditionalOptions", type: "object", required: false, desc: "Extra options based on other answers or region. Keys: zone_{value} or region_{region_key}" },
      { name: "showWhen", type: "object", required: false, desc: "Conditional visibility based on another field's value" },
    ],
  },
  {
    title: "showWhen Condition",
    description: "Controls when a field group is visible based on another field's current value.",
    example: `{ "field": "presence_level", "values": ["background", "active"] }`,
    fields: [
      { name: "field", type: "string", required: true, desc: "The answer key to check" },
      { name: "values", type: "string[]", required: true, desc: "Show this field only when the checked field has one of these values" },
    ],
  },
  {
    title: "Constraints Object",
    description: "Category-based rules applied at runtime. Keyed by product category. Hides invalid options and sets defaults.",
    example: `{
  "hvac_indoor_unit": {
    "hide_options": { "zone_key": ["exterior_facade"], "placement_height": ["floor"] },
    "defaults": { "zone_key": "interior_living_space", "placement_height": "high" }
  }
}`,
    fields: [
      { name: "hide_options", type: "object", required: false, desc: "Map of fieldKey → array of option keys to remove" },
      { name: "defaults", type: "object", required: false, desc: "Map of fieldKey → default value when this category is active" },
    ],
  },
  {
    title: "promptTemplate",
    description: "Template string with placeholders. Brief field answers come from promptFragments. Region context via {{_region_context}}. Product data from Product JSON. Photographer direction from Photographer JSON via {{_photographer_direction}}.",
    example: `"A photorealistic photograph.\\n\\nSETTING: {{_region_context}}. {{zone_key}} of a {{household_type}}.\\nSEASON & LIGHT: {{season}}, {{time_of_day}}.\\n{{_photographer_direction}}\\nPRODUCT: {{_product_description}}.\\n{{prompt}}"`,
    fields: [
      { name: "{{field_key}}", type: "placeholder", required: false, desc: "Replaced with text from promptFragments[fieldKey][selectedValue]" },
      { name: "{{_region_context}}", type: "placeholder", required: false, desc: "Replaced with regionPromptFragment from the brief's root object" },
      { name: "{{_product_description}}", type: "placeholder", required: false, desc: "From Product JSON → productDescription" },
      { name: "{{_product_details}}", type: "placeholder", required: false, desc: "Auto-generated from Product JSON — dimensions, colors, texture, placement. Uses promptFragment from select options." },
      { name: "{{_reference_image_list}}", type: "placeholder", required: false, desc: "Auto-generated from Product JSON → referenceImages" },
      { name: "{{_photographer_direction}}", type: "placeholder", required: false, desc: "Assembled from Photographer JSON — iterates settings, joins each option's promptFragment" },
      { name: "{{prompt}}", type: "placeholder", required: false, desc: "User's free-text notes from a textarea step" },
    ],
  },
  {
    title: "promptFragments",
    description: "Maps each field key + option key → descriptive text that is injected VERBATIM into the prompt. These values ARE the prompt content — write them as complete, descriptive natural language that the AI image engine will read directly. Use Markdown formatting for clarity. Region context is provided by the top-level regionPromptFragment field, not here.",
    example: `{
  "zone_key": {
    "interior_living_space": "The living space (living room, dining area...)",
    "utility": "A utility or functional space"
  },
  "season": {
    "spring": "springtime — fresh greens, blossoms...",
    "winter": "winter — cool blue tones, snow outside..."
  }
}`,
    fields: [
      { name: "[fieldKey]", type: "object", required: true, desc: "Top-level key matching a step's fieldKey or field.key" },
      { name: "[optionKey]", type: "string", required: true, desc: "Nested key → the EXACT descriptive text injected into the prompt. Write as complete, natural-language prose — this is what the AI reads." },
    ],
  },
  {
    title: "Available Icons",
    description: "Icon keys that map to Lucide React components. Use these string values in the step icon field.",
    example: null,
    fields: [
      { name: "home", type: "icon", required: false, desc: "Home / domestic context" },
      { name: "sun", type: "icon", required: false, desc: "Time / lighting / season" },
      { name: "users", type: "icon", required: false, desc: "People / human presence" },
      { name: "eye", type: "icon", required: false, desc: "Style / realism" },
      { name: "camera", type: "icon", required: false, desc: "Composition / framing" },
      { name: "ruler", type: "icon", required: false, desc: "Mounting / dimensions" },
      { name: "message-square", type: "icon", required: false, desc: "Notes / text input" },
      { name: "thermometer", type: "icon", required: false, desc: "Temperature / HVAC" },
      { name: "wind", type: "icon", required: false, desc: "Airflow / ventilation" },
      { name: "zap", type: "icon", required: false, desc: "Energy / power" },
      { name: "droplets", type: "icon", required: false, desc: "Water / humidity" },
      { name: "flame", type: "icon", required: false, desc: "Heating / fire" },
      { name: "snowflake", type: "icon", required: false, desc: "Cooling / winter" },
    ],
  },
];

const productSchemaReference = [
  {
    title: "Root Object (Product JSON)",
    description: "A form schema that defines what inputs to collect about a product. The Product JSON does NOT contain product data — it defines the form UI that collects it. Sections render as collapsible panels; fields render as the appropriate input control.",
    example: `{
  "version": "1.0",
  "productLabel": "Product Specification",
  "sections": [
    {
      "id": "identity",
      "title": "Product Identity",
      "icon": "package",
      "fields": [
        { "key": "product_type", "label": "Product Type", "type": "text", "required": true, "placeholder": "..." },
        { "key": "brand", "label": "Brand", "type": "text" }
      ]
    }
  ]
}`,
    fields: [
      { name: "version", type: "string", required: false, desc: "Schema version" },
      { name: "productLabel", type: "string", required: false, desc: "Heading shown in the form preview" },
      { name: "sections", type: "Section[]", required: true, desc: "Ordered array of form sections" },
    ],
  },
  {
    title: "Section Object",
    description: "A collapsible group of related fields. Each section appears as a panel in the product form.",
    example: `{
  "id": "dimensions",
  "title": "Physical Dimensions",
  "icon": "ruler",
  "description": "Exact measurements for proportional rendering",
  "fields": [
    { "key": "width_cm", "label": "Width", "type": "number", "unit": "cm", "placeholder": "80" }
  ]
}`,
    fields: [
      { name: "id", type: "string", required: true, desc: "Unique section identifier" },
      { name: "title", type: "string", required: true, desc: "Section heading" },
      { name: "icon", type: "string", required: false, desc: "Lucide icon key (e.g. 'package', 'ruler', 'paintbrush', 'camera')" },
      { name: "description", type: "string", required: false, desc: "Helper text shown below the section title" },
      { name: "fields", type: "Field[]", required: true, desc: "Array of form fields in this section" },
    ],
  },
  {
    title: "Field Object",
    description: "A single form input. The 'type' determines the rendered control.",
    example: `{ "key": "brand", "label": "Brand", "type": "text", "required": false, "placeholder": "e.g. Daikin" }`,
    fields: [
      { name: "key", type: "string", required: true, desc: "Unique field key — used to store the collected value" },
      { name: "label", type: "string", required: true, desc: "Display label for the input" },
      { name: "type", type: "string", required: true, desc: "Field type — see Field Types below" },
      { name: "required", type: "boolean", required: false, desc: "If true, shows a red asterisk indicator" },
      { name: "placeholder", type: "string", required: false, desc: "Placeholder text for text/number/textarea fields" },
      { name: "unit", type: "string", required: false, desc: "Unit suffix shown next to number fields (e.g. 'cm', 'kg')" },
      { name: "options", type: "Option[]", required: false, desc: "Options for 'select' type fields" },
      { name: "slots", type: "ImageSlot[]", required: false, desc: "Image upload slots for 'image_slot' type fields" },
    ],
  },
  {
    title: "Field Types",
    description: "The type field determines how the input is rendered.",
    example: null,
    fields: [
      { name: "text", type: "type", required: false, desc: "Single-line text input. Use for names, colors, short descriptions." },
      { name: "number", type: "type", required: false, desc: "Numeric input with optional unit suffix. Multiple number fields in a section render in a compact grid." },
      { name: "textarea", type: "type", required: false, desc: "Multi-line text input. Use for descriptions and detailed notes." },
      { name: "select", type: "type", required: false, desc: "Dropdown selector. Requires 'options' array with { key, label } objects." },
      { name: "tag_list", type: "type", required: false, desc: "User-managed list of string tags. Items added via Enter key. Use for features, rules, etc." },
      { name: "image_slot", type: "type", required: false, desc: "Reference image upload slots. Requires 'slots' array with { tag, label, description }." },
    ],
  },
  {
    title: "Select Option",
    description: "A choice within a 'select' dropdown field.",
    example: `{ "key": "matte", "label": "Matte", "promptFragment": "Matte finish — flat, non-reflective surface" }`,
    fields: [
      { name: "key", type: "string", required: true, desc: "Value stored when selected" },
      { name: "label", type: "string", required: true, desc: "Display text in the dropdown" },
      { name: "promptFragment", type: "string", required: false, desc: "Text injected into the prompt when this option is selected. Replaces hardcoded translation maps." },
    ],
  },
  {
    title: "ImageSlot Object",
    description: "A reference image upload slot. The tag maps to product_images.tag in the database. These images are ALWAYS attached to AI requests.",
    example: `{ "tag": "front", "label": "Front View", "description": "Product from the front — main identifying angle" }`,
    fields: [
      { name: "tag", type: "string", required: true, desc: "Image tag — maps to product_images.tag (e.g. 'front', 'side', 'angle')" },
      { name: "label", type: "string", required: true, desc: "Human-readable label" },
      { name: "description", type: "string", required: true, desc: "What this angle/view should show" },
    ],
  },
];

const photographerSchemaReference = [
  {
    title: "Root Object (Photographer JSON)",
    description: "Defines the photographic style and camera behavior applied to a render. This is purely about HOW the image is shot — not WHAT is in it (that's the Brief) or WHAT the product looks like (that's the Product JSON).",
    example: `{
  "version": "1.0",
  "name": "Clean Editorial",
  "description": "Magazine-quality precision...",
  "emoji": "📐",
  "settings": {
    "camera_type": "editorial",
    "lens_style": "standard_35mm",
    "lens_effects": "clean",
    "camera_framing": "eye_level_wide",
    "focus_balance": "balanced",
    "surface_realism": "pristine",
    "atmosphere": "none"
  },
  "fieldDefinitions": [...]
}`,
    fields: [
      { name: "version", type: "string", required: true, desc: "Schema version, e.g. \"1.0\"" },
      { name: "name", type: "string", required: true, desc: "Human-readable style name" },
      { name: "description", type: "string", required: false, desc: "What this style achieves" },
      { name: "emoji", type: "string", required: false, desc: "Visual identifier for the style" },
      { name: "settings", type: "object", required: true, desc: "Key-value pairs — each key matches a fieldDefinitions entry" },
      { name: "fieldDefinitions", type: "FieldDef[]", required: true, desc: "Self-documenting array of all valid options per setting key" },
    ],
  },
  {
    title: "Settings Keys",
    description: "The settings object contains the active values. Each key maps to a fieldDefinition that documents all valid options.",
    example: null,
    fields: [
      { name: "camera_type", type: "string", required: true, desc: "Shooting approach: documentary, editorial, architectural, lifestyle" },
      { name: "lens_style", type: "string", required: true, desc: "Focal length: wide_24mm, standard_35mm, normal_50mm, tele_85mm" },
      { name: "lens_effects", type: "string", required: true, desc: "Lens character: clean, natural, vintage, anamorphic" },
      { name: "camera_framing", type: "string", required: true, desc: "Shot framing: wide, eye_level_wide, detail" },
      { name: "focus_balance", type: "string", required: true, desc: "Subject vs environment weight: environment_first, balanced, subject_forward" },
      { name: "surface_realism", type: "string", required: true, desc: "Surface condition: pristine, subtle_wear, lived_in, weathered" },
      { name: "atmosphere", type: "string", required: true, desc: "Atmospheric effects: none, dust_motes, seasonal, steam_warmth" },
    ],
  },
  {
    title: "fieldDefinitions Entry",
    description: "Each entry documents a single setting key with all its valid options. Makes the config self-describing and editable without external docs.",
    example: `{
  "key": "camera_type",
  "label": "Camera Type",
  "options": [
    { "key": "editorial", "label": "Editorial", "description": "Composed but natural", "promptFragment": "Editorial photography style — composed but natural, magazine-quality..." }
  ]
}`,
    fields: [
      { name: "key", type: "string", required: true, desc: "Matches a key in the settings object" },
      { name: "label", type: "string", required: true, desc: "Human-readable label for this setting" },
      { name: "options", type: "Option[]", required: true, desc: "All valid values with labels, descriptions, and promptFragments" },
      { name: "options[].promptFragment", type: "string", required: true, desc: "The exact text injected into the prompt via {{_photographer_direction}} when this option is active" },
    ],
  },
];

const watchExample = `{
  "region_key": null,
  "region_label": null,
  "regionPromptFragment": "",
  "steps": [
    {
      "id": "setting",
      "title": "Scene Setting",
      "subtitle": "Where should the watch be photographed?",
      "icon": "camera",
      "type": "tile_select",
      "fieldKey": "scene_setting",
      "options": [
        { "key": "wrist_lifestyle", "label": "On Wrist — Lifestyle", "emoji": "⌚", "description": "Worn casually, coffee shop, walking in the city" },
        { "key": "wrist_formal", "label": "On Wrist — Formal", "emoji": "🤵", "description": "Suit sleeve, boardroom, evening event" },
        { "key": "flat_lay", "label": "Flat Lay", "emoji": "📐", "description": "Watch on a surface — marble, leather, wood" },
        { "key": "display_stand", "label": "Display Stand", "emoji": "🏛️", "description": "Boutique-style presentation, dramatic lighting" },
        { "key": "adventure", "label": "Adventure / Outdoor", "emoji": "🏔️", "description": "Hiking, diving, sports — rugged context" }
      ]
    },
    {
      "id": "surface",
      "title": "Surface Material",
      "subtitle": "What sits beneath or around the watch?",
      "icon": "eye",
      "type": "tile_select",
      "fieldKey": "surface_material",
      "options": [
        { "key": "marble_white", "label": "White Marble", "description": "Clean luxury, high contrast" },
        { "key": "dark_leather", "label": "Dark Leather", "description": "Rich, textured, classic feel" },
        { "key": "brushed_metal", "label": "Brushed Metal", "description": "Modern, industrial, reflective" },
        { "key": "natural_wood", "label": "Natural Wood", "description": "Warm, organic, Scandinavian" },
        { "key": "concrete", "label": "Raw Concrete", "description": "Brutalist, urban, editorial" }
      ]
    },
    {
      "id": "lighting",
      "title": "Lighting Mood",
      "subtitle": "Set the visual atmosphere",
      "icon": "sun",
      "type": "tile_select",
      "fieldKey": "lighting_mood",
      "options": [
        { "key": "soft_natural", "label": "Soft Natural", "emoji": "🌤️", "description": "Window light, gentle shadows" },
        { "key": "dramatic", "label": "Dramatic", "emoji": "🌑", "description": "Hard shadows, spotlight, high contrast" },
        { "key": "golden_hour", "label": "Golden Hour", "emoji": "🌅", "description": "Warm sunset tones, long shadows" },
        { "key": "studio_clean", "label": "Studio Clean", "emoji": "💡", "description": "Even, diffused, no harsh shadows" }
      ]
    },
    {
      "id": "notes",
      "title": "Creative Direction",
      "subtitle": "Any additional instructions",
      "icon": "message-square",
      "type": "textarea",
      "fieldKey": "prompt",
      "placeholder": "E.g. 'Show the chronograph subdials clearly' or 'Include a glass of whiskey in the background'..."
    }
  ],
  "constraints": {}
}`;

const SchemaSection = ({ section }: { section: { title: string; description: string; example: string | null; fields: { name: string; type: string; required: boolean; desc: string }[] } }) => (
  <Card>
    <CardContent className="pt-6">
      <h3 className="text-base font-semibold mb-1">{section.title}</h3>
      <p className="text-sm text-muted-foreground mb-4">{section.description}</p>

      {section.example && (
        <pre className="bg-muted/50 border border-border rounded-lg p-4 text-xs font-mono leading-relaxed mb-4 overflow-x-auto max-h-[400px] overflow-y-auto">
          {section.example}
        </pre>
      )}

      <div className="border border-border rounded-lg overflow-hidden">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-border bg-muted/30">
              <th className="text-left px-3 py-2 font-medium text-xs">Field</th>
              <th className="text-left px-3 py-2 font-medium text-xs">Type</th>
              <th className="text-left px-3 py-2 font-medium text-xs">Req.</th>
              <th className="text-left px-3 py-2 font-medium text-xs">Description</th>
            </tr>
          </thead>
          <tbody>
            {section.fields.map((f) => (
              <tr key={f.name} className="border-b border-border last:border-0">
                <td className="px-3 py-2 font-mono text-xs text-primary">{f.name}</td>
                <td className="px-3 py-2 font-mono text-xs text-muted-foreground">{f.type}</td>
                <td className="px-3 py-2 text-xs">{f.required ? "✓" : "—"}</td>
                <td className="px-3 py-2 text-xs text-muted-foreground">{f.desc}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </CardContent>
  </Card>
);

function serializeReference(): string {
  const lines: string[] = [];
  lines.push("# JSON Reference — Brief JSON + Product JSON + Photographer JSON");
  lines.push("");

  // ── System Overview ──
  lines.push("## 🏗️ System Overview — What This Is and Why");
  lines.push("");
  lines.push("This system generates photorealistic product-in-context images using AI. The entire creative direction is defined by **three JSON configuration files** that are combined at generation time into a single text prompt sent to an AI image engine.");
  lines.push("");
  lines.push("### The Problem It Solves");
  lines.push("Product marketing teams need hundreds of lifestyle images showing their products in realistic home/commercial settings across different regions, seasons, and styles. Traditional photography is expensive, slow, and doesn't scale. This system lets non-technical users configure every aspect of the desired image through guided wizard UIs, with AI generating the final visuals.");
  lines.push("");
  lines.push("### Architecture: Three JSONs → One Prompt");
  lines.push("");
  lines.push("The system splits creative direction into three independent axes, each defined by its own JSON:");
  lines.push("");
  lines.push("| JSON | Axis | Question It Answers | What It Produces |");
  lines.push("|------|------|---------------------|------------------|");
  lines.push("| **Brief** | Scene & Context | *Where* is the product shown? | Wizard UI with steps/options + a `promptTemplate` with `{{placeholders}}` + `promptFragments` that map selections to descriptive text |");
  lines.push("| **Product** | Physical Specs | *What* does the product look like? | Form schema for collecting dimensions, color, texture, placement rules, reference images. Select options carry `promptFragment` text. |");
  lines.push("| **Photographer** | Camera & Style | *How* is the image shot? | Pre-built style presets with `settings` + `fieldDefinitions`. Each option carries a `promptFragment`. |");
  lines.push("");
  lines.push("### How Prompt Assembly Works");
  lines.push("");
  lines.push("```");
  lines.push("┌─────────────┐   ┌──────────────┐   ┌────────────────┐");
  lines.push("│  Brief JSON │   │ Product JSON  │   │ Photographer   │");
  lines.push("│             │   │              │   │ JSON           │");
  lines.push("│ promptTmpl  │   │ form answers │   │ settings +     │");
  lines.push("│ + fragments │   │ + fragments  │   │ fragments      │");
  lines.push("└──────┬──────┘   └──────┬───────┘   └───────┬────────┘");
  lines.push("       │                │                    │");
  lines.push("       └────────────────┼────────────────────┘");
  lines.push("                       ▼");
  lines.push("              ┌─────────────────┐");
  lines.push("              │ Template Engine  │");
  lines.push("              │ (pure replace)   │");
  lines.push("              └────────┬────────┘");
  lines.push("                       ▼");
  lines.push("              ┌─────────────────┐");
  lines.push("              │  Final Prompt    │");
  lines.push("              │  (sent to AI)    │");
  lines.push("              └─────────────────┘");
  lines.push("```");
  lines.push("");
  lines.push("1. User fills out the **Brief wizard** (selecting room type, season, etc.) → answers stored as `{ field_key: selected_option_key }`");
  lines.push("2. User fills out the **Product form** (entering dimensions, selecting finish, uploading reference photos)");
  lines.push("3. User picks a **Photographer style** (a preset that defines camera behavior)");
  lines.push("4. At generation time, the **template engine**:");
  lines.push("   - Takes the Brief's `promptTemplate` as the skeleton");
  lines.push("   - Replaces `{{field_key}}` with text from `promptFragments[field_key][selected_value]`");
  lines.push("   - Replaces `{{_region_context}}` with the Brief's `regionPromptFragment`");
  lines.push("   - Replaces `{{_product_description}}` and `{{_product_details}}` with assembled Product data");
  lines.push("   - Replaces `{{_reference_image_list}}` with reference image metadata");
  lines.push("   - Replaces `{{_photographer_direction}}` with joined Photographer `promptFragment` values");
  lines.push("   - Sends the fully resolved string + reference images to the AI");
  lines.push("");
  lines.push("### Key Design Principles");
  lines.push("");
  lines.push("1. **JSON = Source of Truth**: There is zero hardcoded prompt logic. All descriptive text lives inside the JSONs. The engine is a dumb template replacer.");
  lines.push("2. **Strict Separation**: Each JSON owns one domain. Content must never leak across boundaries (see Separation Rules below).");
  lines.push("3. **No Repetition, No Contradiction**: The same concept must appear in exactly ONE JSON. Repeating it (even rephrased) creates conflicts and confuses the AI. Contradictory instructions across JSONs (e.g. Brief says 'busy kitchen' while Photographer says 'clean minimal background') produce incoherent results.");
  lines.push("4. **Prompt-Ready Text**: All `promptFragment` and `promptTemplate` values are written as natural language the AI reads directly. No codes, no abbreviations, no post-processing.");
  lines.push("5. **Self-Describing**: Each JSON contains its own schema (options, labels, descriptions) so it can be edited without external documentation.");
  lines.push("6. **Composable**: Any Brief can combine with any Product and any Photographer style. This only works if each JSON stays in its lane.");
  lines.push("");
  lines.push("### For Third-Party Tool Builders");
  lines.push("");
  lines.push("If you are building a tool that generates these JSONs:");
  lines.push("- Each JSON type has a fixed schema documented below. Follow it exactly.");
  lines.push("- The Brief JSON defines both the UI (wizard steps) AND the prompt content (template + fragments). Your tool must generate both.");
  lines.push("- The Product JSON defines a form schema, NOT product data. It describes what inputs to collect. Actual product data is entered by users at runtime.");
  lines.push("- The Photographer JSON is a self-contained style preset. The `settings` object holds active values; `fieldDefinitions` documents all valid options with their `promptFragment` text.");
  lines.push("- All three JSONs are stored in a single `wizard_configs` database table, differentiated by a `type` column (`brief`, `product`, `photographer`).");
  lines.push("- Brief JSONs can be linked to specific Product and Photographer configs via a `config_links` junction table, restricting which combinations are valid.");
  lines.push("- Test composability: generate one of each type and verify they combine into a coherent prompt without repetition or contradiction.");
  lines.push("");
  lines.push("---");
  lines.push("");

  lines.push("## ⚠️ Critical: Prompt-Ready Content");
  lines.push("");
  lines.push("Every piece of text in these JSONs that ends up in the prompt is **the final prompt content**. There is **NO post-processing**, NO translation layer, NO hardcoded logic between the JSON and the AI. The system is a **pure template engine** that:");
  lines.push("1. Loads the three JSONs");
  lines.push("2. Replaces `{{placeholders}}` with the corresponding text from the JSONs");
  lines.push("3. Sends the result directly to the AI image engine");
  lines.push("");
  lines.push("This means:");
  lines.push("- **`promptFragments` values** in Brief JSON → injected verbatim into the prompt");
  lines.push("- **`promptFragment` on each option** in Product JSON (select fields) → injected verbatim");
  lines.push("- **`promptFragment` on each option** in Photographer JSON → joined and injected verbatim");
  lines.push("- **`regionPromptFragment`** → injected verbatim via `{{_region_context}}`");
  lines.push("- **`promptTemplate`** → the skeleton that holds everything together");
  lines.push("");
  lines.push("If a value is missing or empty, the placeholder resolves to an empty string. No fallback descriptions exist outside the JSONs.");
  lines.push("");
  lines.push("### Markdown Formatting");
  lines.push("The final assembled prompt should use **Markdown formatting** (headers, bold, bullet points) for optimal readability by the AI image engine. Use Markdown in your `promptTemplate` and `promptFragment` values where it improves clarity — e.g. `**SETTING:**`, `- bullet points`, `### Section Headers`.");
  lines.push("");
  lines.push("---");
  lines.push("");
  lines.push("## Three JSONs, One Prompt — Separation of Concerns");
  lines.push("");
  lines.push("Three separate JSON configs each own a distinct concern:");
  lines.push("- **Brief JSON**: WHAT to show — wizard UI (steps, options, constraints) + prompt template with {{field_key}} placeholders + fragment mappings. Each brief targets ONE region or is region-free. Covers scene context: room type, zone, season, time of day, human presence.");
  lines.push("- **Product JSON**: WHAT the product looks like — form schema for collecting product details (dimensions, appearance, texture, color, placement/mounting rules) + reference images (always attached to AI). Select field options carry a `promptFragment` that is injected verbatim into `{{_product_details}}`.");
  lines.push("- **Photographer JSON**: HOW to shoot it — camera type, lens, framing, focus balance, surface realism, atmosphere. Each option carries a `promptFragment` that is joined and injected verbatim into `{{_photographer_direction}}`. No scene content, no product specs, no placement rules.");
  lines.push("");
  lines.push("---");
  lines.push("");
  lines.push("## ⛔ STRICT SEPARATION — DO NOT MIX");
  lines.push("");
  lines.push("Each JSON owns ONE domain. Content from one domain MUST NEVER appear in another JSON. This is the most important rule when generating new configs.");
  lines.push("");
  lines.push("### 🔁 No Repetition / No Contradiction");
  lines.push("");
  lines.push("- **No Repetition**: The same information must appear in exactly ONE JSON. Even if rephrased, paraphrased, or summarized differently, duplicating a concept across JSONs creates redundancy that confuses the AI and makes maintenance impossible.");
  lines.push("- **No Contradiction**: Instructions in one JSON must never conflict with instructions in another. Because all three are merged into a single prompt, contradictory statements (e.g. Brief says 'cozy cluttered kitchen' while Photographer says 'clean minimal background') produce incoherent or unpredictable images.");
  lines.push("- **Test**: Before shipping a new config, mentally assemble all three JSONs and read the result as one text. If any concept appears twice or any two statements fight each other, fix it.");
  lines.push("");
  lines.push("### Brief JSON");
  lines.push("- **MUST contain**: scene context — room type, zone, season, time of day, human presence, region-specific architecture/vegetation/light");
  lines.push("- **MUST NEVER contain**: camera settings, lens info, framing instructions, focus balance, surface realism, atmosphere effects, product dimensions, product color/texture/finish, mounting/placement rules, reference image slots");
  lines.push("");
  lines.push("### Product JSON");
  lines.push("- **MUST contain**: physical product specs — dimensions, color, finish, texture, mounting position, placement rules, reference image upload slots");
  lines.push("- **MUST NEVER contain**: scene context (room types, zones, seasons, time of day), camera/lens settings, framing, photographer direction, atmosphere, focus balance");
  lines.push("");
  lines.push("### Photographer JSON");
  lines.push("- **MUST contain**: camera type, lens style, lens effects, framing, focus balance, surface realism, atmosphere");
  lines.push("- **MUST NEVER contain**: scene context (room types, zones, seasons), product specs (dimensions, color, texture, finish), placement/mounting rules, region context, human presence");
  lines.push("");
  lines.push("### Ownership Table");
  lines.push("| Concern | Belongs In | NEVER In |");
  lines.push("|---------|-----------|----------|");
  lines.push("| Room type, zone, season, time of day | Brief | Product, Photographer |");
  lines.push("| Region-specific architecture/context | Brief | Product, Photographer |");
  lines.push("| Human presence, lifestyle context | Brief | Product, Photographer |");
  lines.push("| Product dimensions, color, texture, finish | Product | Brief, Photographer |");
  lines.push("| Mounting position, placement rules | Product | Brief, Photographer |");
  lines.push("| Reference images | Product | Brief, Photographer |");
  lines.push("| Camera type, lens, framing | Photographer | Brief, Product |");
  lines.push("| Focus balance (subject vs environment) | Photographer | Brief, Product |");
  lines.push("| Surface wear, atmosphere effects | Photographer | Brief, Product |");
  lines.push("");
  lines.push("### ❌ Common Mistakes to Avoid");
  lines.push("1. Putting camera framing or lens instructions in a Brief `promptFragment` — these belong in Photographer JSON only");
  lines.push("2. Putting room/scene descriptions in a Photographer `promptFragment` — scene context belongs in Brief JSON only");
  lines.push("3. Putting placement/mounting rules in a Photographer config — placement is a physical product attribute, belongs in Product JSON only");
  lines.push("4. Duplicating product dimensions or color in the Brief `promptTemplate` — product data is injected via `{{_product_details}}` from Product JSON");
  lines.push("5. Adding \"fill the entire frame\" or \"clean background\" to a Photographer style that will be used with in-situ placement — this contradicts the room context from Brief JSON");
  lines.push("6. Putting region-specific content (e.g. Norwegian cabin types) in Product or Photographer JSON — region context belongs in Brief JSON only");
  lines.push("7. Repeating the same concept in different words across JSONs — e.g. Brief says 'warm afternoon light' AND Photographer says 'golden warm lighting'. Pick ONE owner.");
  lines.push("");

  const serializeSection = (sections: typeof briefSchemaReference, heading: string) => {
    lines.push(`## ${heading}`);
    lines.push("");
    for (const s of sections) {
      lines.push(`### ${s.title}`);
      lines.push(s.description);
      if (s.example) {
        lines.push("```json");
        lines.push(s.example);
        lines.push("```");
      }
      lines.push("");
      lines.push("| Field | Type | Required | Description |");
      lines.push("|-------|------|----------|-------------|");
      for (const f of s.fields) {
        lines.push(`| \`${f.name}\` | ${f.type} | ${f.required ? "✓" : "—"} | ${f.desc} |`);
      }
      lines.push("");
    }
  };

  serializeSection(briefSchemaReference, "Brief JSON Reference");
  lines.push("### Example: Watch Brief Config (Region-Free)");
  lines.push("```json");
  lines.push(watchExample);
  lines.push("```");
  lines.push("");
  serializeSection(productSchemaReference, "Product JSON Reference");
  serializeSection(photographerSchemaReference, "Photographer JSON Reference");

  return lines.join("\n");
}

export default function WizardJsonReference() {
  const [copied, setCopied] = useState(false);

  const handleCopyAll = async () => {
    const text = serializeReference();
    await navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="max-w-4xl mx-auto pb-12">
      <div className="mb-8 flex items-start justify-between">
        <div>
          <h1 className="text-xl font-semibold">JSON Reference</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Complete schema reference for the three JSON configs that drive visual generation: <strong className="text-foreground">Brief</strong> (what to show), <strong className="text-foreground">Product</strong> (what the product looks like), and <strong className="text-foreground">Photographer</strong> (how to shoot it).
          </p>
        </div>
        <Button variant="outline" size="sm" onClick={handleCopyAll} className="shrink-0 ml-4">
          {copied ? <CheckCheck className="w-3.5 h-3.5 mr-1" /> : <Copy className="w-3.5 h-3.5 mr-1" />}
          {copied ? "Copied!" : "Copy All"}
        </Button>
      </div>

      {/* System Overview */}
      <Card className="mb-6 border-accent/30">
        <CardContent className="pt-6">
          <h2 className="text-base font-semibold mb-2">🏗️ System Overview — What This Is and Why</h2>
          <div className="text-sm text-muted-foreground space-y-3">
            <p>
              This system generates <strong className="text-foreground">photorealistic product-in-context images</strong> using AI. The entire creative direction is defined by <strong className="text-foreground">three JSON configuration files</strong> that are combined at generation time into a single text prompt sent to an AI image engine.
            </p>
            <p>
              Product marketing teams need hundreds of lifestyle images showing products in realistic settings across regions, seasons, and styles. This system lets non-technical users configure every aspect through guided wizard UIs, with AI generating the final visuals.
            </p>
            <div className="rounded-lg border border-border bg-muted/30 p-3">
              <p className="text-xs font-semibold text-foreground mb-2">Architecture: Three JSONs → One Prompt</p>
              <div className="overflow-x-auto">
                <table className="text-xs w-full">
                  <thead>
                    <tr className="border-b border-border">
                      <th className="text-left px-2 py-1 font-medium">JSON</th>
                      <th className="text-left px-2 py-1 font-medium">Axis</th>
                      <th className="text-left px-2 py-1 font-medium">Question</th>
                      <th className="text-left px-2 py-1 font-medium">Output</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr className="border-b border-border">
                      <td className="px-2 py-1 font-semibold text-foreground">Brief</td>
                      <td className="px-2 py-1">Scene & Context</td>
                      <td className="px-2 py-1 italic">Where is the product shown?</td>
                      <td className="px-2 py-1">Wizard UI + promptTemplate + promptFragments</td>
                    </tr>
                    <tr className="border-b border-border">
                      <td className="px-2 py-1 font-semibold text-foreground">Product</td>
                      <td className="px-2 py-1">Physical Specs</td>
                      <td className="px-2 py-1 italic">What does it look like?</td>
                      <td className="px-2 py-1">Form schema + select option promptFragments</td>
                    </tr>
                    <tr>
                      <td className="px-2 py-1 font-semibold text-foreground">Photographer</td>
                      <td className="px-2 py-1">Camera & Style</td>
                      <td className="px-2 py-1 italic">How is it shot?</td>
                      <td className="px-2 py-1">Style presets + fieldDefinition promptFragments</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
            <div className="rounded-lg border border-border bg-muted/30 p-3">
              <p className="text-xs font-semibold text-foreground mb-2">How Prompt Assembly Works</p>
              <ol className="text-xs space-y-1 list-decimal list-inside ml-1">
                <li>User fills out the <strong>Brief wizard</strong> → answers stored as <code className="bg-muted px-1 rounded">{"{ field_key: selected_option_key }"}</code></li>
                <li>User fills out the <strong>Product form</strong> → dimensions, finish, reference photos</li>
                <li>User picks a <strong>Photographer style</strong> → a preset defining camera behavior</li>
                <li>Template engine takes the Brief's <code className="bg-muted px-1 rounded">promptTemplate</code> and replaces all <code className="bg-muted px-1 rounded">{"{{placeholders}}"}</code> with resolved text from all three JSONs</li>
                <li>Fully resolved string + reference images sent to AI</li>
              </ol>
            </div>
            <div className="rounded-lg border border-primary/20 bg-primary/5 p-3">
              <p className="text-xs font-semibold text-foreground mb-2">Key Design Principles</p>
              <ul className="text-xs space-y-1 list-disc list-inside ml-1">
                <li><strong>JSON = Source of Truth</strong> — zero hardcoded prompt logic; all text lives in the JSONs</li>
                <li><strong>Strict Separation</strong> — each JSON owns one domain; content never leaks across</li>
                <li><strong>No Repetition, No Contradiction</strong> — same concept in exactly ONE JSON; conflicting instructions produce incoherent images</li>
                <li><strong>Prompt-Ready Text</strong> — all fragments are natural language the AI reads directly</li>
                <li><strong>Self-Describing</strong> — each JSON contains its own schema (options, labels, descriptions)</li>
                <li><strong>Composable</strong> — any Brief + any Product + any Photographer style; only works if each stays in its lane</li>
              </ul>
            </div>
            <div className="rounded-lg border border-border bg-muted/30 p-3">
              <p className="text-xs font-semibold text-foreground mb-2">For Third-Party Tool Builders</p>
              <ul className="text-xs space-y-1 list-disc list-inside ml-1">
                <li>Each JSON type has a fixed schema documented below — follow it exactly</li>
                <li>Brief JSON defines both UI (wizard steps) AND prompt content (template + fragments)</li>
                <li>Product JSON defines a <em>form schema</em>, not product data — it describes what inputs to collect</li>
                <li>Photographer JSON is a self-contained style preset with <code className="bg-muted px-1 rounded">settings</code> + <code className="bg-muted px-1 rounded">fieldDefinitions</code></li>
                <li>All three stored in a single <code className="bg-muted px-1 rounded">wizard_configs</code> table, differentiated by <code className="bg-muted px-1 rounded">type</code> column</li>
                <li>Brief JSONs can be linked to specific Product/Photographer configs via a <code className="bg-muted px-1 rounded">config_links</code> junction table</li>
                <li>Test composability: generate one of each and verify they combine without repetition or contradiction</li>
              </ul>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Prompt-Ready Content principle */}
      <Card className="mb-6 border-primary/30">
        <CardContent className="pt-6">
          <h2 className="text-base font-semibold mb-2">🎯 Prompt-Ready Content — The #1 Rule</h2>
          <div className="text-sm text-muted-foreground space-y-3">
            <p>
              Every piece of text in these JSONs that ends up in the prompt is <strong className="text-foreground">the final prompt content</strong>. There is <strong className="text-foreground">NO post-processing</strong>, no translation layer, no hardcoded logic between the JSON and the AI.
            </p>
            <p>The system is a <strong className="text-foreground">pure template engine</strong> that:</p>
            <ol className="list-decimal list-inside space-y-1 ml-2">
              <li>Loads the three JSONs</li>
              <li>Replaces <code className="text-xs bg-muted px-1 rounded">{"{{placeholders}}"}</code> with the corresponding text from the JSONs</li>
              <li>Sends the result directly to the AI image engine</li>
            </ol>
            <div className="mt-3 rounded-lg border border-border bg-muted/30 p-3 space-y-2">
              <p className="text-xs font-medium text-foreground">What this means when creating a new JSON:</p>
              <ul className="text-xs space-y-1 list-disc list-inside ml-1">
                <li><code className="bg-muted px-1 rounded">promptFragments</code> values in Brief → injected <strong>verbatim</strong> into the prompt</li>
                <li><code className="bg-muted px-1 rounded">promptFragment</code> on each Product select option → injected <strong>verbatim</strong> into <code className="bg-muted px-1 rounded">{"{{_product_details}}"}</code></li>
                <li><code className="bg-muted px-1 rounded">promptFragment</code> on each Photographer option → joined and injected <strong>verbatim</strong> into <code className="bg-muted px-1 rounded">{"{{_photographer_direction}}"}</code></li>
                <li><code className="bg-muted px-1 rounded">regionPromptFragment</code> → injected <strong>verbatim</strong> via <code className="bg-muted px-1 rounded">{"{{_region_context}}"}</code></li>
                <li>If a value is missing or empty, the placeholder resolves to an empty string. <strong>No fallback exists outside the JSONs.</strong></li>
              </ul>
            </div>
            <div className="mt-3 rounded-lg border border-primary/20 bg-primary/5 p-3">
              <p className="text-xs font-medium text-foreground mb-1">📝 Markdown Formatting</p>
              <p className="text-xs">The final assembled prompt should use <strong>Markdown formatting</strong> (headers, bold, bullet points) for optimal readability by the AI image engine. Use Markdown in your <code className="bg-muted px-1 rounded">promptTemplate</code> and <code className="bg-muted px-1 rounded">promptFragment</code> values — e.g. <code className="bg-muted px-1 rounded">**SETTING:**</code>, <code className="bg-muted px-1 rounded">- bullet points</code>, <code className="bg-muted px-1 rounded">### Section Headers</code>.</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Concept overview */}
      <Card className="mb-6">
        <CardContent className="pt-6">
          <h2 className="text-base font-semibold mb-2">💡 Three JSONs, One Prompt — Separation of Concerns</h2>
          <div className="text-sm text-muted-foreground space-y-3">
            <p>
              The system uses <strong className="text-foreground">three separate JSON configurations</strong>, each owning a distinct axis:
            </p>
            <ul className="list-disc list-inside space-y-1 ml-2">
              <li><strong className="text-foreground">Brief JSON</strong> — <em>WHAT to show.</em> Wizard UI (steps, options, constraints) and prompt template. Scene context: room type, zone, season, time of day, human presence. Each brief targets one region or is region-free.</li>
              <li><strong className="text-foreground">Product JSON</strong> — <em>WHAT the product looks like.</em> Form schema for collecting dimensions, appearance, texture, color, placement/mounting rules, and reference images. Select field options carry a <code className="bg-muted px-1 rounded text-xs">promptFragment</code> injected verbatim.</li>
              <li><strong className="text-foreground">Photographer JSON</strong> — <em>HOW to shoot it.</em> Camera type, lens, framing, focus balance, surface realism, atmosphere. Each option carries a <code className="bg-muted px-1 rounded text-xs">promptFragment</code> injected verbatim. No scene content, no product specs, no placement rules.</li>
            </ul>
          </div>
        </CardContent>
      </Card>

      {/* STRICT SEPARATION */}
      <Card className="mb-6 border-destructive/50">
        <CardContent className="pt-6">
          <h2 className="text-base font-semibold mb-2">⛔ STRICT SEPARATION — DO NOT MIX</h2>
          <div className="text-sm text-muted-foreground space-y-4">
            <p>Each JSON owns <strong className="text-foreground">ONE domain</strong>. Content from one domain <strong className="text-foreground">MUST NEVER</strong> appear in another JSON.</p>

            <div className="rounded-lg border border-destructive/30 bg-destructive/5 p-3">
              <p className="text-xs font-semibold text-foreground mb-1">🔁 No Repetition / No Contradiction</p>
              <ul className="text-xs space-y-1 list-disc list-inside ml-1">
                <li><strong>No Repetition</strong>: The same information must appear in exactly ONE JSON. Even rephrased or summarized differently, duplicating a concept creates redundancy that confuses the AI.</li>
                <li><strong>No Contradiction</strong>: Instructions in one JSON must never conflict with another. All three merge into one prompt — contradictory statements produce incoherent images.</li>
                <li><strong>Test</strong>: Before shipping, mentally assemble all three JSONs and read as one text. If any concept appears twice or any two statements fight, fix it.</li>
              </ul>
            </div>

            <div className="space-y-3">
              <div className="rounded-lg border border-border bg-muted/30 p-3">
                <p className="text-xs font-semibold text-foreground mb-1">Brief JSON</p>
                <p className="text-xs"><strong className="text-green-600">MUST contain:</strong> scene context — room type, zone, season, time of day, human presence, region-specific architecture/vegetation/light</p>
                <p className="text-xs mt-1"><strong className="text-destructive">MUST NEVER contain:</strong> camera settings, lens info, framing, focus balance, surface realism, atmosphere, product dimensions, color/texture/finish, mounting/placement rules, reference image slots</p>
              </div>
              <div className="rounded-lg border border-border bg-muted/30 p-3">
                <p className="text-xs font-semibold text-foreground mb-1">Product JSON</p>
                <p className="text-xs"><strong className="text-green-600">MUST contain:</strong> physical product specs — dimensions, color, finish, texture, mounting position, placement rules, reference image upload slots</p>
                <p className="text-xs mt-1"><strong className="text-destructive">MUST NEVER contain:</strong> scene context (room types, zones, seasons, time of day), camera/lens settings, framing, photographer direction, atmosphere, focus balance</p>
              </div>
              <div className="rounded-lg border border-border bg-muted/30 p-3">
                <p className="text-xs font-semibold text-foreground mb-1">Photographer JSON</p>
                <p className="text-xs"><strong className="text-green-600">MUST contain:</strong> camera type, lens style, lens effects, framing, focus balance, surface realism, atmosphere</p>
                <p className="text-xs mt-1"><strong className="text-destructive">MUST NEVER contain:</strong> scene context (room types, zones, seasons), product specs (dimensions, color, texture, finish), placement/mounting rules, region context, human presence</p>
              </div>
            </div>

            <div className="rounded-lg border border-destructive/30 bg-destructive/5 p-3">
              <p className="text-xs font-semibold text-foreground mb-2">❌ Common Mistakes to Avoid</p>
              <ol className="text-xs space-y-1 list-decimal list-inside ml-1">
                <li>Putting camera framing or lens instructions in a Brief <code className="bg-muted px-1 rounded">promptFragment</code></li>
                <li>Putting room/scene descriptions in a Photographer <code className="bg-muted px-1 rounded">promptFragment</code></li>
                <li>Putting placement/mounting rules in a Photographer config</li>
                <li>Duplicating product dimensions or color in Brief <code className="bg-muted px-1 rounded">promptTemplate</code></li>
                <li>Adding "fill the entire frame" or "clean background" to a Photographer style used with in-situ placement</li>
                <li>Putting region-specific content in Product or Photographer JSON</li>
                <li>Repeating the same concept in different words across JSONs — e.g. Brief says 'warm afternoon light' AND Photographer says 'golden warm lighting'</li>
              </ol>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* ═══════ BRIEF JSON REFERENCE ═══════ */}
      <div className="mb-8">
        <div className="flex items-center gap-2 mb-4">
          <div className="h-px flex-1 bg-border" />
          <h2 className="text-sm font-semibold uppercase tracking-wider text-muted-foreground px-3">Brief JSON Reference</h2>
          <div className="h-px flex-1 bg-border" />
        </div>
        <div className="space-y-6">
          {briefSchemaReference.map((section) => (
            <SchemaSection key={section.title} section={section} />
          ))}
        </div>
      </div>

      {/* Example: Watch brief config */}
      <Card className="mb-8">
        <CardContent className="pt-6">
          <h2 className="text-base font-semibold mb-1">Example: Watch Brief Config (Region-Free)</h2>
          <p className="text-sm text-muted-foreground mb-4">
            A completely different wizard for luxury watch photography — no region needed. Different steps, different options, same Brief JSON schema. Paste this into the Wizard Builder to preview it.
          </p>
          <pre className="bg-muted/50 border border-border rounded-lg p-4 text-xs font-mono leading-relaxed overflow-x-auto max-h-[400px] overflow-y-auto">
            {watchExample}
          </pre>
        </CardContent>
      </Card>

      {/* ═══════ PRODUCT JSON REFERENCE ═══════ */}
      <div className="mb-8">
        <div className="flex items-center gap-2 mb-4">
          <div className="h-px flex-1 bg-border" />
          <h2 className="text-sm font-semibold uppercase tracking-wider text-muted-foreground px-3">Product JSON Reference</h2>
          <div className="h-px flex-1 bg-border" />
        </div>
        <div className="space-y-6">
          {productSchemaReference.map((section) => (
            <SchemaSection key={section.title} section={section} />
          ))}
        </div>
      </div>

      {/* ═══════ PHOTOGRAPHER JSON REFERENCE ═══════ */}
      <div className="mb-8">
        <div className="flex items-center gap-2 mb-4">
          <div className="h-px flex-1 bg-border" />
          <h2 className="text-sm font-semibold uppercase tracking-wider text-muted-foreground px-3">Photographer JSON Reference</h2>
          <div className="h-px flex-1 bg-border" />
        </div>
        <div className="space-y-6">
          {photographerSchemaReference.map((section) => (
            <SchemaSection key={section.title} section={section} />
          ))}
        </div>
      </div>
    </div>
  );
}
